import { createApiResponse, createErrorApiResponse } from "@entrilia/shared/api/axiosHelper.ts";
import { getClientCode } from "@entrilia/shared/api/clientApiContext";
import type { FileDownloadInfo } from "@entrilia/shared/api/fileTypes";
import { getDefaultHeaders } from "@entrilia/shared/api/headers.ts";
import { parseSseStream } from "@entrilia/shared/api/sseStream";
import type { ApiResponse, BooleanOperationResult, ListWithTotal } from "@entrilia/shared/api/types";
import type { AnyObject } from "@entrilia/shared/types.ts";
import { getBackendBaseUrl } from "@entrilia/shared/variables";
import type { AxiosError } from "axios";
import axios from "axios";
import qs from "qs";
import { dataSetHeader } from "../components/pages/reporting/sv/api/dataSetApiContext.ts";
import {
  toBiAgentMessageContract,
  toBuildExportContract,
  toBuildReportContract,
  toRunExportContract,
  toRunReportContract,
  toUserInputContract,
} from "./biUserInputContracts";
import type { BiAgentUiResponse, SendBiAgentMessageRequest } from "./types/biAgentTypes";
import type { FieldConfiguration } from "./types/fieldConfigurationTypes.ts";
import type {
  RecentSmartViewArtifact,
  RunSmartViewArtifactSourceRequest,
  SendSmartViewArtifactMessageRequest,
  SmartViewArtifact,
  SmartViewArtifactAgentMode,
  SmartViewArtifactAgentStreamEvent,
  SmartViewArtifactSourceData,
  SmartViewArtifactVersion,
  SmartViewArtifactVersionSummary,
} from "./types/smartViewArtifactsTypes";
import type {
  AddReportGroupRequest,
  AddReportTemplateRequest,
  AnyReportConfigurationForRunning,
  ApArAgingDetailResponse,
  ApArAgingSummaryResponse,
  ApiReportResponse,
  AttachedDocument,
  BalanceSheetDataResponse,
  BuildApArAgingDetailExportRequest,
  BuildApArAgingDetailReportRequest,
  BuildApArAgingSummaryExportRequest,
  BuildApArAgingSummaryReportRequest,
  BuildBalanceSheetExportRequest,
  BuildBalanceSheetReportRequest,
  BuildChartReportRequest,
  BuildExportRequest,
  BuildFinancialStatementReportRequest,
  BuildListReportRequest,
  BuildPivotReportRequest,
  BuildTabularReportRequest,
  BuildTileReportRequest,
  CancellationToken,
  CashFlowExportRequest,
  ChangeReportGroupRequest,
  ChangeReportsGroupRequest,
  ChartOfAccounts,
  ChartOfAccountsDataResponse,
  ChartOfAccountsExportRequest,
  ChartResponse,
  ClientSettings,
  ClientsResponse,
  CopyReportRequest,
  CopyReportResponse,
  CreateMeasureSubgroupRequest,
  CreateReportGroup,
  CrossFilterRequest,
  CrossFilterResponse,
  CustomMeasureDescription,
  DataModelResponse,
  DbSchemaResponse,
  DeleteReportGroupResult,
  DeleteReportsRequest,
  DeleteReportTemplatesRequest,
  DictionarySearchRequest,
  DictionarySearchResponse,
  DimensionDictionary,
  DimensionQueryInfo,
  DimensionsResponse,
  DrillDownCacheValidationResponse,
  DrillDownConfiguration,
  DrillDownDataRequest,
  DrillDownDataResponse,
  DrillDownInfoResult,
  DuplicateReportRequest,
  DuplicateTemplateRequest,
  ExportResponse,
  FinancialStatementResponse,
  GenerateApiReportRequest,
  GenerateDashboardReportRequest,
  GetReportAuthorizationResponse,
  GetReportTemplateForCompanyRequest,
  GetReportTemplateForOrganizationRequest,
  GetTemplateFieldsDataRequest,
  LedgerBasisRequest,
  LedgerBasisResponse,
  ListDataResponse,
  MeasureDataset,
  MeasureDescriptor,
  MeasureSubgroupWithMeasures,
  MetaDataResponse,
  MoveCustomMeasureToSubgroupRequest,
  NameValidationResponse,
  OrganizationReportInfo,
  PivotDataResponse,
  RenameReportRequest,
  ReorderReportGroupsRequest,
  ReplaceReportGroupRequest,
  Report,
  ReportAuthorizationInfo,
  ReportGroup,
  ReportGroupsResponse,
  ReportHistoryItem,
  ReportInfo,
  ReportResponse,
  ReportTemplate,
  ReportTemplateDto,
  ReportType,
  ReportUsage,
  ReportValidationResponse,
  RunChartReportRequest,
  RunExportRequest,
  RunFinancialStatementReportRequest,
  RunListReportRequest,
  RunPivotReportRequest,
  RunTabularReportRequest,
  RunTileReportRequest,
  TabularDataResponse,
  TabularReportConfigurationForRunning,
  TabularSnapshotPageRequest,
  TemplateInfoFieldsResponse,
  TemplateNameValidationResponse,
  TileDataResponse,
  UpdateMeasureSubgroupRequest,
  UpdateReportAuthorizationRequest,
  UpdateReportGroupAuthorizationRequest,
  UpdateReportGroupRequest,
  UpdateReportsAuthorizationRequest,
  UpdateReportTemplateDataSourceRequest,
  UpdateReportTemplateDataSourceResponse,
  UpdateReportTemplateRequest,
  UpdateReportTemplateStatusRequest,
  UsedByReportsResponse,
  UsedTablesResponse,
  ValidateReportNameRequest,
  ValidateReportRequest,
  ValidateTemplateNameRequest,
} from "./types/svTypes.ts";
import type {
  CancelJobsResponse,
  CancelWorkbookRunsRequest,
  DeleteWorkbookRunsRequest,
  DeleteWorkbookTemplatesRequest,
  DownloadWorkbooksRequest,
  ParameterDictionaryResponse,
  ReportAccessStateResponse,
  ReportsAccessStateRequest,
  ReportSource,
  RunWorkbookRequest,
  RunWorkbookResponse,
  SearchWorkbookRunsRequest,
  SearchWorkbookTemplatesRequest,
  WorkbookRun,
  WorkbookRunsProgress,
  WorkbookTemplate,
} from "./types/workbookRuns.types.ts";

interface ReportsApiClient {
  validateReport: (
    body: ValidateReportRequest,
    done: (data: ApiResponse<ReportValidationResponse>) => void,
    error: (reason: string) => void
  ) => CancellationToken;
  duplicateReport: (request: DuplicateReportRequest) => Promise<ApiResponse<ReportInfo>>;
  copyReport: (
    body: CopyReportRequest,
    done: (data: ApiResponse<CopyReportResponse>) => void,
    error: (reason: string) => void
  ) => CancellationToken;
  addReportGroups: (groups: CreateReportGroup[]) => Promise<ApiResponse<ReportInfo[]>>;
  updateReportsSharing: (request: UpdateReportsAuthorizationRequest) => Promise<ApiResponse<BooleanOperationResult>>;
}
export interface UiReportsApiClient extends ReportsApiClient {
  renameReport: typeof uiReportsApi.renameReport;
  changeReportGroup: typeof uiReportGroupApi.changeReportGroup;
  changeReportsGroup: typeof uiReportGroupApi.changeReportsGroup;
  deleteReports: typeof uiReportsApi.deleteReports;
  generateApiReport: typeof generateApiReport;
  generateDashboardReport: typeof generateDashboardReport;
}

export interface SystemReportsApiClient extends ReportsApiClient {
  renameReport: typeof systemReportsApi.renameReport;
  changeReportGroup: typeof systemReportGroupApi.changeReportGroup;
  changeReportsGroup: typeof systemReportGroupApi.changeReportsGroup;
  deleteReports: typeof systemReportsApi.deleteReports;
}

export interface ApiReportsApiClient extends ReportsApiClient {
  renameReport: typeof apiReportsApi.renameReport;
  changeReportGroup: typeof apiReportGroupApi.changeReportGroup;
  changeReportsGroup: typeof apiReportGroupApi.changeReportsGroup;
  deleteReports: typeof apiReportsApi.deleteReports;
}

type EndPoints = {
  Reports: string;
  Tabular: string;
  Pivot: string | undefined;
  Tile: string | undefined;
  Chart: string | undefined;
  List: string | undefined;
  FinancialStatement: string | undefined;
};

const endPointsSetup: Record<ReportUsage, EndPoints> = {
  Ui: {
    Reports: "UiReports",
    Tabular: "UiTabular",
    Pivot: "UiPivot",
    Chart: "UiChart",
    Tile: "UiTile",
    List: "UiList",
    FinancialStatement: "UiFinancialStatement",
  },
  Api: {
    Reports: "ApiReports",
    Tabular: "ApiTabular",
    Pivot: undefined,
    Tile: undefined,
    Chart: undefined,
    List: undefined,
    FinancialStatement: undefined,
  },
  Dashboard: {
    Reports: "DashboardReports",
    Tabular: "DashboardTabular",
    Pivot: "DashboardPivot",
    Tile: "DashboardTile",
    Chart: "DashboardChart",
    List: undefined,
    FinancialStatement: undefined,
  },
  System: {
    Reports: "SystemReports",
    Tabular: "SystemTabular",
    Pivot: "SystemPivot",
    Chart: "SystemChart",
    Tile: "SystemTile",
    List: undefined,
    FinancialStatement: undefined,
  },
};

const reportTemplateGroups = "ReportTemplateGroups";

const baseUri = getBackendBaseUrl("reporting");

const builderHeaders = () => {
  return {
    ...getDefaultHeaders(),
    ...dataSetHeader(),
  };
};

const getDataSetHeader = (dataSetId: string) => ({ ["X-Entrilia-BI-DataSet"]: dataSetId });

export const validateUniqueReportNameByUsage = async (request: ValidateReportNameRequest, usage: ReportUsage) => {
  const endpoint = endPointsSetup[usage].Reports;
  const { data } = await axios.post<ApiResponse<NameValidationResponse>>(
    `${baseUri}/${endpoint}/validate-report-name`,
    toUserInputContract(request, ["name"]),
    {
      headers: getDefaultHeaders(),
    }
  );
  return data;
};

export const validateTemplateName = async (request: ValidateTemplateNameRequest) => {
  const { data } = await axios.post<ApiResponse<TemplateNameValidationResponse>>(
    `${baseUri}/ReportTemplates/validate-template-name`,
    toUserInputContract(request, ["name"]),
    {
      headers: getDefaultHeaders(),
    }
  );
  return data;
};
export const createReportTemplateDraft = async (clientCode: string) => {
  const { data } = await axios.post<ApiResponse<ReportTemplate>>(
    `${baseUri}/ReportTemplates/${clientCode}/create-draft`,
    undefined,
    {
      headers: getDefaultHeaders(),
    }
  );
  return data;
};

export const addReportTemplate = async (request: AddReportTemplateRequest) => {
  const { data } = await axios.post<ApiResponse<ReportTemplate>>(
    `${baseUri}/ReportTemplates`,
    { ...request, template: toUserInputContract(request.template, ["name", "configuration", "description"]) },
    {
      headers: getDefaultHeaders(),
    }
  );
  return data;
};
export const getOrganizationReportTemplates = async () => {
  const { data } = await axios.get<ApiResponse<ReportTemplateDto[]>>(`${baseUri}/ReportTemplates/organization`, {
    headers: getDefaultHeaders(),
  });
  return data;
};
export const getCompanyReportTemplates = async () => {
  const { data } = await axios.get<ApiResponse<ReportTemplateDto[]>>(`${baseUri}/ReportTemplates/company`, {
    headers: getDefaultHeaders(),
  });
  return data;
};
export const getReportTemplateForCompany = async (request: GetReportTemplateForCompanyRequest) => {
  const { data } = await axios.get<ApiResponse<ReportTemplate>>(
    `${baseUri}/ReportTemplates/${request.templateId}/company`,
    {
      headers: getDefaultHeaders(),
    }
  );
  return data;
};
export const getReportTemplateForOrganization = async (request: GetReportTemplateForOrganizationRequest) => {
  const { data } = await axios.get<ApiResponse<ReportTemplate>>(
    `${baseUri}/ReportTemplates/${request.templateId}/${request.organization}/organization`,
    {
      headers: getDefaultHeaders(),
    }
  );
  return data;
};
export const updateReportTemplate = async (request: UpdateReportTemplateRequest) => {
  const { data } = await axios.put<ApiResponse<ReportTemplate>>(
    `${baseUri}/ReportTemplates/${request.template.reportId}`,
    { ...request, template: toUserInputContract(request.template, ["name", "configuration", "description"]) },
    {
      headers: getDefaultHeaders(),
    }
  );
  return data;
};
export const updateReportTemplateStatus = async (request: UpdateReportTemplateStatusRequest) => {
  const { data } = await axios.put<ApiResponse<ReportTemplate>>(
    `${baseUri}/ReportTemplates/${request.templateId}/status`,
    { status: request.status },
    {
      headers: getDefaultHeaders(),
    }
  );
  return data;
};
export const updateReportTemplateDataSource = async (request: UpdateReportTemplateDataSourceRequest) => {
  const { data } = await axios.put<ApiResponse<UpdateReportTemplateDataSourceResponse>>(
    `${baseUri}/ReportTemplates/${request.templateId}/datasource`,
    { dataSource: request.dataSource },
    {
      headers: getDefaultHeaders(),
    }
  );
  return data;
};
export const deleteReportTemplates = async (request: DeleteReportTemplatesRequest) => {
  const { data } = await axios.patch<ApiResponse<boolean>>(`${baseUri}/ReportTemplates/bulk-delete`, request, {
    headers: getDefaultHeaders(),
  });
  return data;
};
export const getClients = async () => {
  const { data } = await axios.get<ApiResponse<ClientsResponse>>(`${baseUri}/Clients`, {
    headers: getDefaultHeaders(),
  });
  return data;
};
export const getTemplateFieldsData = async (request: GetTemplateFieldsDataRequest) => {
  const { data } = await axios.get<ApiResponse<TemplateInfoFieldsResponse>>(
    `${baseUri}/ReportTemplates/${request.templateId}/info-fields`,
    {
      headers: getDefaultHeaders(),
    }
  );
  return data;
};
export const duplicateReportTemplate = async (request: DuplicateTemplateRequest) => {
  const { data } = await axios.post<ApiResponse<ReportTemplate | undefined>>(
    `${baseUri}/ReportTemplates/duplicate/${request.templateId}`,
    { nameUserInput: request.name, groupId: request.groupId, organization: request.organization },
    {
      headers: getDefaultHeaders(),
    }
  );
  return data;
};
export const crossFilter = (
  dataSetId: string,
  body: CrossFilterRequest,
  done: (data: ApiResponse<CrossFilterResponse>) => void,
  error: (reason: string) => void
): CancellationToken => {
  const controller = new AbortController();
  axios
    .post<ApiResponse<CrossFilterResponse>>(`${baseUri}/CrossFiltering`, toRunReportContract(body), {
      headers: { ...getDefaultHeaders(), ...getDataSetHeader(dataSetId) },
      signal: controller.signal,
    })
    .then((res) => done(res.data))
    .catch((reason: Error) => error(reason.message));
  return { cancel: () => controller.abort() };
};
export const getBlankColumnFieldIds = async (
  dataSetId: string,
  reportType: "Tabular" | "Pivot",
  fields: FieldConfiguration[],
  signal?: AbortSignal
): Promise<ApiResponse<string[]>> => {
  const { data } = await axios.post<ApiResponse<string[]>>(
    `${baseUri}/MetaData/blankColumnFields/${reportType.toLowerCase()}/user-input`,
    { fieldsUserInput: fields },
    { headers: { ...getDefaultHeaders(), ...getDataSetHeader(dataSetId) }, signal }
  );
  return data;
};

export const searchDimensionDictionary = async (
  dataSetId: string,
  body: DictionarySearchRequest,
  signal?: AbortSignal
) => {
  const { data } = await axios.post<ApiResponse<DictionarySearchResponse>>(
    `${baseUri}/MetaData/dictionary/search`,
    toUserInputContract(body, ["conditions"]),
    { headers: { ...getDefaultHeaders(), ...getDataSetHeader(dataSetId) }, signal }
  );
  return data;
};
export const loadDimensionDictionaries = async (dataSetId: string, ...dimensions: string[]) => {
  const queryParams = new URLSearchParams();
  dimensions.map((d) => queryParams.append("d", d));
  const { data } = await axios.get<ApiResponse<{ [key: string]: DimensionDictionary }[]>>(
    `${baseUri}/MetaData/dictionaries?${queryParams.toString()}`,
    {
      headers: { ...getDefaultHeaders(), ...getDataSetHeader(dataSetId) },
    }
  );
  return data;
};

export const loadMeta = async () => {
  const { data } = await axios.get<ApiResponse<MetaDataResponse>>(`${baseUri}/MetaData`, {
    headers: builderHeaders(),
  });
  return data;
};

export const buildBalanceSheetReport = async (request: BuildBalanceSheetReportRequest, signal?: AbortSignal) => {
  const { data } = await axios.post<ApiResponse<BalanceSheetDataResponse>>(
    `${baseUri}/BalanceSheetReport/build`,
    toBuildReportContract(request),
    {
      headers: builderHeaders(),
      signal,
    }
  );
  return data;
};

export const buildApArAgingSummaryReport = async (
  request: BuildApArAgingSummaryReportRequest,
  signal?: AbortSignal
) => {
  const { data } = await axios.post<ApiResponse<ApArAgingSummaryResponse>>(
    `${baseUri}/ApArAgingSummaryReport/build`,
    toBuildReportContract(request),
    { headers: builderHeaders(), signal }
  );
  return data;
};

export const requestBuildApArAgingSummaryExport = async (body: BuildApArAgingSummaryExportRequest) => {
  const { data } = await axios.post<ApiResponse<ExportResponse>>(
    `${baseUri}/ApArAgingSummaryReport/buildExport`,
    toBuildExportContract(body),
    {
      headers: builderHeaders(),
    }
  );
  return data;
};

export const buildApArAgingDetailReport = async (request: BuildApArAgingDetailReportRequest, signal?: AbortSignal) => {
  const { data } = await axios.post<ApiResponse<ApArAgingDetailResponse>>(
    `${baseUri}/ApArAgingDetailReport/build`,
    toBuildReportContract(request),
    { headers: builderHeaders(), signal }
  );
  return data;
};

export const requestBuildApArAgingDetailExport = async (body: BuildApArAgingDetailExportRequest) => {
  const { data } = await axios.post<ApiResponse<ExportResponse>>(
    `${baseUri}/ApArAgingDetailReport/buildExport`,
    toBuildExportContract(body),
    {
      headers: builderHeaders(),
    }
  );
  return data;
};

export const getBalanceSheetDimensionValues = async (dimension: string, signal?: AbortSignal) => {
  const { data } = await axios.get<ApiResponse<DimensionDictionary[]>>(
    `${baseUri}/BalanceSheetReport/dimension-values?dimension=${encodeURIComponent(dimension)}`,
    {
      headers: builderHeaders(),
      signal,
    }
  );
  return data;
};

export const requestBuildBalanceSheetExport = async (body: BuildBalanceSheetExportRequest) => {
  const { data } = await axios.post<ApiResponse<ExportResponse>>(
    `${baseUri}/BalanceSheetReport/buildExport`,
    toBuildExportContract(body),
    {
      headers: builderHeaders(),
    }
  );
  return data;
};

const buildTileReportBase = (
  body: BuildTileReportRequest,
  done: (data: ApiResponse<TileDataResponse>) => void,
  error: (reason: string | undefined) => void,
  endpoint: string
): CancellationToken => {
  const controller = new AbortController();
  axios
    .post<ApiResponse<TileDataResponse>>(`${baseUri}/${endpoint}/build`, toBuildReportContract(body), {
      headers: builderHeaders(),
      signal: controller.signal,
    })
    .then((res) => done(res.data))
    .catch((reason: Error) => {
      if (reason.message === "canceled") {
        error("canceled");
        return;
      }
      const axiosError = reason as AxiosError<ApiResponse<unknown>>;
      const errorMessage = axiosError.response?.data?.error?.message;
      error(errorMessage);
    });
  return { cancel: () => controller.abort() };
};

const buildPivotReportBase = (
  body: BuildPivotReportRequest,
  done: (data: ApiResponse<PivotDataResponse>) => void,
  error: (reason: string | undefined) => void,
  endpoint: string
): CancellationToken => {
  const controller = new AbortController();
  axios
    .post<ApiResponse<PivotDataResponse>>(`${baseUri}/${endpoint}/build`, toBuildReportContract(body), {
      headers: builderHeaders(),
      signal: controller.signal,
    })
    .then((res) => done(res.data))
    .catch((reason: Error) => {
      if (reason.message === "canceled") {
        error("canceled");
        return;
      }
      const axiosError = reason as AxiosError<ApiResponse<unknown>>;
      const errorMessage = axiosError.response?.data?.error?.message;
      error(errorMessage);
    });
  return { cancel: () => controller.abort() };
};

const buildChartReportBase = (
  body: BuildChartReportRequest,
  done: (data: ApiResponse<ChartResponse>) => void,
  error: (reason: string | undefined) => void,
  endpoint: string
): CancellationToken => {
  const controller = new AbortController();
  axios
    .post<ApiResponse<ChartResponse>>(`${baseUri}/${endpoint}/build`, toBuildReportContract(body), {
      headers: builderHeaders(),
      signal: controller.signal,
    })
    .then((res) => done(res.data))
    .catch((reason: Error) => {
      if (reason.message === "canceled") {
        error("canceled");
        return;
      }
      const axiosError = reason as AxiosError<ApiResponse<ChartResponse>>;
      const errorMessage = axiosError.response?.data?.error?.message;
      error(errorMessage);
    });
  return { cancel: () => controller.abort() };
};
export const getDrillDownData = async (body: DrillDownDataRequest, signal?: AbortSignal) => {
  const { data } = await axios.post<ApiResponse<DrillDownDataResponse>>(
    `${baseUri}/DrillDown`,
    toRunReportContract(body),
    {
      headers: builderHeaders(),
      signal: signal,
    }
  );
  return data;
};
export const getDrillDownItem = async (sessionId: string, itemId: string) => {
  const { data } = await axios.get<ApiResponse<DrillDownInfoResult>>(
    `${baseUri}/DrillDown/item?sessionId=${sessionId}&itemId=${itemId}`,
    {
      headers: builderHeaders(),
    }
  );
  return data;
};
export const getDrillDownConfiguration = async () => {
  const { data } = await axios.get<ApiResponse<DrillDownConfiguration>>(`${baseUri}/DrillDown/configuration`, {
    headers: builderHeaders(),
  });
  return data;
};
export const getAttachedDocuments = async (transactionNo: string, signal?: AbortSignal) => {
  const params = new URLSearchParams({ transactionNo });
  const { data } = await axios.get<ApiResponse<AttachedDocument[]>>(
    `${baseUri}/DrillDown/attached-documents?${params.toString()}`,
    { headers: builderHeaders(), signal }
  );
  return data;
};
export const getAttachedDocumentDownloadInfo = async (catalogueId: string, signal?: AbortSignal) => {
  const { data } = await axios.get<ApiResponse<FileDownloadInfo>>(
    `${baseUri}/DrillDown/attached-documents/${catalogueId}/download`,
    { headers: builderHeaders(), signal }
  );
  return data;
};
export const getChartOfAccountsDrillDown = async (sessionId: string, itemId: string) => {
  const { data } = await axios.get<ApiResponse<ChartOfAccountsDataResponse>>(
    `${baseUri}/DrillDown/chartofaccounts?sessionId=${sessionId}&itemId=${itemId}`,
    {
      headers: builderHeaders(),
    }
  );
  return data;
};
export const isDrillDownCacheValid = async (sessionId: string, etag: string) => {
  const { data } = await axios.get<ApiResponse<DrillDownCacheValidationResponse>>(
    `${baseUri}/DrillDown/validatecache?sessionId=${sessionId}&etag=${etag}`,
    {
      headers: builderHeaders(),
    }
  );
  return data;
};
export const requestDrillDownExport = async (body: DrillDownDataRequest) => {
  const { data } = await axios.post<ApiResponse<ExportResponse>>(
    `${baseUri}/DrillDown/export`,
    toRunReportContract(body),
    {
      headers: builderHeaders(),
    }
  );
  return data;
};
export const requestCashflowExport = async (body: CashFlowExportRequest) => {
  const { data } = await axios.post<ApiResponse<ExportResponse>>(
    `${baseUri}/DrillDown/export/cashflow`,
    toUserInputContract(body, ["caption"]),
    {
      headers: builderHeaders(),
    }
  );
  return data;
};
export const requestChartOfAccountsExport = async (body: ChartOfAccountsExportRequest) => {
  const { data } = await axios.post<ApiResponse<ExportResponse>>(
    `${baseUri}/DrillDown/export/chartofaccounts`,
    toUserInputContract(body, ["caption"]),
    {
      headers: builderHeaders(),
    }
  );
  return data;
};
export const checkExportReady = async (token: string) => {
  const { data } = await axios.get<ApiResponse<ExportResponse>>(`${baseUri}/ReportFiles/export?token=${token}`, {
    headers: builderHeaders(),
  });
  return data;
};
export const generateExportDownloadLink = (token: string) => {
  return `${baseUri}/ReportFiles/export/download?token=${token}&clientCode=${getClientCode()}`;
};
export const addCustomMeasure = async (measure: CustomMeasureDescription) => {
  const { data } = await axios.post<ApiResponse<MeasureDescriptor>>(
    `${baseUri}/CustomMeasure`,
    toUserInputContract(measure, ["name", "caption", "group", "formula", "units", "configuration"]),
    {
      headers: builderHeaders(),
    }
  );
  return data;
};
export const updateCustomMeasure = async (id: string, measure: CustomMeasureDescription) => {
  const { data } = await axios.put<ApiResponse<MeasureDescriptor>>(
    `${baseUri}/CustomMeasure/${id}`,
    toUserInputContract(measure, ["name", "caption", "group", "formula", "units", "configuration"]),
    {
      headers: builderHeaders(),
    }
  );
  return data;
};
export const deleteCustomMeasure = async (id: string) => {
  const { data } = await axios.delete<ApiResponse<boolean>>(`${baseUri}/CustomMeasure/${id}`, {
    headers: builderHeaders(),
  });
  return data;
};
export const lockCustomMeasure = async (id: string) => {
  const { data } = await axios.post<ApiResponse<MeasureDescriptor>>(`${baseUri}/CustomMeasure/${id}/lock`, undefined, {
    headers: builderHeaders(),
  });
  return data;
};
export const unlockCustomMeasure = async (id: string) => {
  const { data } = await axios.post<ApiResponse<MeasureDescriptor>>(
    `${baseUri}/CustomMeasure/${id}/unlock`,
    undefined,
    {
      headers: builderHeaders(),
    }
  );
  return data;
};
export const createMeasureSubgroup = async (request: CreateMeasureSubgroupRequest) => {
  const { data } = await axios.post<ApiResponse<MeasureSubgroupWithMeasures>>(
    `${baseUri}/CustomMeasure/subgroups`,
    toUserInputContract(request, ["name"]),
    {
      headers: builderHeaders(),
    }
  );
  return data;
};
export const updateMeasureSubgroup = async (id: string, request: UpdateMeasureSubgroupRequest) => {
  const { data } = await axios.put<ApiResponse<MeasureSubgroupWithMeasures>>(
    `${baseUri}/CustomMeasure/subgroups/${id}`,
    toUserInputContract(request, ["name"]),
    {
      headers: builderHeaders(),
    }
  );
  return data;
};
export const deleteMeasureSubgroup = async (id: string) => {
  const { data } = await axios.delete<ApiResponse<BooleanOperationResult>>(`${baseUri}/CustomMeasure/subgroups/${id}`, {
    headers: builderHeaders(),
  });
  return data;
};
export const moveCustomMeasureToSubgroup = async (measureId: string, subgroupId: string | undefined) => {
  const request: MoveCustomMeasureToSubgroupRequest = { subgroupId: subgroupId ?? null };
  const { data } = await axios.post<ApiResponse<MeasureDescriptor>>(
    `${baseUri}/CustomMeasure/${measureId}/subgroup`,
    request,
    {
      headers: builderHeaders(),
    }
  );
  return data;
};
export const getUsedByReports = async (id: string) => {
  const { data } = await axios.get<ApiResponse<UsedByReportsResponse>>(`${baseUri}/CustomMeasure/${id}/used-by`, {
    headers: builderHeaders(),
  });
  return data;
};
export const getMeasureDataSets = async () => {
  const { data } = await axios.get<ApiResponse<MeasureDataset[]>>(`${baseUri}/CustomMeasure/datasets`, {
    headers: builderHeaders(),
  });
  return data;
};
export const getLedgerBasis = (
  body: LedgerBasisRequest,
  done: (data: ApiResponse<LedgerBasisResponse>) => void,
  error: (reason: string) => void
): CancellationToken => {
  const controller = new AbortController();
  axios
    .post<ApiResponse<LedgerBasisResponse>>(`${baseUri}/${endPointsSetup.Ui.Reports}/ledger-basis`, body, {
      headers: builderHeaders(),
      signal: controller.signal,
    })
    .then((res) => done(res.data))
    .catch((reason: Error) => error(reason.message));
  return { cancel: () => controller.abort() };
};

export const getChartOfAccounts = async () => {
  const { data } = await axios.get<ApiResponse<ChartOfAccounts>>(`${baseUri}/ChartOfAccounts`, {
    headers: builderHeaders(),
  });
  return data;
};

export const getClientSettings = async () => {
  const { data } = await axios.get<ApiResponse<ClientSettings>>(`${baseUri}/Clients/settings`, {
    headers: builderHeaders(),
  });
  return data;
};

const buildTabularReportBase = <
  TBody extends BuildTabularReportRequest,
  TResponse extends TabularDataResponse | ApiReportResponse,
>(
  body: TBody,
  done: (data: ApiResponse<TResponse>) => void,
  error: (reason: string | undefined) => void,
  endpoint: string
): CancellationToken => {
  const controller = new AbortController();
  axios
    .post<ApiResponse<TabularDataResponse>>(`${baseUri}/${endpoint}/build`, toBuildReportContract(body), {
      headers: builderHeaders(),
      signal: controller.signal,
    })
    .then((res) => done(res.data as ApiResponse<TResponse>))
    .catch((reason: Error) => {
      if (reason.message === "canceled") {
        error("canceled");
        return;
      }
      const axiosError = reason as AxiosError<ApiResponse<unknown>>;
      const errorMessage = axiosError.response?.data?.error?.message;
      error(errorMessage);
    });
  return { cancel: () => controller.abort() };
};

const generateApiReport = async (request: GenerateApiReportRequest) => {
  const { data } = await axios.post<ApiResponse<ReportInfo>>(
    `${baseUri}/${endPointsSetup.Ui.Reports}/generate-api-report`,
    toUserInputContract(request, ["name"]),
    {
      headers: getDefaultHeaders(),
    }
  );
  return data;
};

const generateDashboardReport = async (request: GenerateDashboardReportRequest) => {
  const { data } = await axios.post<ApiResponse<ReportInfo>>(
    `${baseUri}/${endPointsSetup.Ui.Reports}/generate-dashboard-report`,
    request,
    {
      headers: getDefaultHeaders(),
    }
  );
  return data;
};

const runTileReportBase = (
  dataSetId: string,
  body: RunTileReportRequest,
  done: (data: ApiResponse<TileDataResponse>) => void,
  error: (reason: string | undefined) => void,
  endpoint: string
): CancellationToken => {
  const controller = new AbortController();
  axios
    .post<ApiResponse<TileDataResponse>>(`${baseUri}/${endpoint}/run`, toRunReportContract(body), {
      headers: { ...getDefaultHeaders(), ...getDataSetHeader(dataSetId) },
      signal: controller.signal,
    })
    .then((res) => done(res.data))
    .catch((reason: Error) => {
      if (reason.message === "canceled") {
        error("canceled");
        return;
      }
      const axiosError = reason as AxiosError<ApiResponse<unknown>>;
      const errorMessage = axiosError.response?.data?.error?.message;
      error(errorMessage);
    });
  return { cancel: () => controller.abort() };
};

const runTabularReportBase = <
  TBody extends RunTabularReportRequest,
  TResponse extends TabularDataResponse | ApiReportResponse,
>(
  dataSetId: string,
  body: TBody,
  done: (data: ApiResponse<TResponse>) => void,
  error: (reason: string | undefined) => void,
  endpoint: string
): CancellationToken => {
  const controller = new AbortController();
  axios
    .post<ApiResponse<TabularDataResponse>>(`${baseUri}/${endpoint}/run`, toRunReportContract(body), {
      headers: { ...getDefaultHeaders(), ...getDataSetHeader(dataSetId) },
      signal: controller.signal,
    })
    .then((res) => done(res.data as ApiResponse<TResponse>))
    .catch((reason: Error) => {
      if (reason.message === "canceled") {
        error("canceled");
        return;
      }
      const axiosError = reason as AxiosError<ApiResponse<unknown>>;
      const errorMessage = axiosError.response?.data?.error?.message;
      error(errorMessage);
    });
  return { cancel: () => controller.abort() };
};

export const getTabularSnapshotPage = async (
  request: TabularSnapshotPageRequest,
  dataSetId: string | undefined,
  signal: AbortSignal
): Promise<ApiResponse<TabularDataResponse>> => {
  const { data } = await axios.post<ApiResponse<TabularDataResponse>>(`${baseUri}/UiTabular/page`, request, {
    headers: { ...getDefaultHeaders(), ...(dataSetId ? getDataSetHeader(dataSetId) : {}) },
    signal,
  });
  return data;
};

const runListReportBase = async (
  dataSetId: string,
  body: RunListReportRequest,
  endpoint: string,
  signal?: AbortSignal
) => {
  const { data } = await axios.post<ApiResponse<ListDataResponse>>(
    `${baseUri}/${endpoint}/run`,
    toRunReportContract(body),
    {
      headers: { ...getDefaultHeaders(), ...getDataSetHeader(dataSetId) },
      signal,
    }
  );
  return data;
};

const buildListReportBase = async (body: BuildListReportRequest, endpoint: string, signal?: AbortSignal) => {
  const { data } = await axios.post<ApiResponse<ListDataResponse>>(
    `${baseUri}/${endpoint}/build`,
    toBuildReportContract(body),
    {
      headers: builderHeaders(),
      signal,
    }
  );
  return data;
};

const runFinancialStatementReportBase = async (
  dataSetId: string,
  body: RunFinancialStatementReportRequest,
  endpoint: string,
  signal?: AbortSignal
) => {
  const { data } = await axios.post<ApiResponse<FinancialStatementResponse>>(
    `${baseUri}/${endpoint}/run`,
    toRunReportContract(body),
    {
      headers: { ...getDefaultHeaders(), ...getDataSetHeader(dataSetId) },
      signal,
    }
  );
  return data;
};

const buildFinancialStatementReportBase = async (
  body: BuildFinancialStatementReportRequest,
  endpoint: string,
  signal?: AbortSignal
) => {
  const { data } = await axios.post<ApiResponse<FinancialStatementResponse>>(
    `${baseUri}/${endpoint}/build`,
    toBuildReportContract(body),
    {
      headers: builderHeaders(),
      signal,
    }
  );
  return data;
};

const runPivotReportBase = (
  dataSetId: string,
  body: RunPivotReportRequest,
  done: (data: ApiResponse<PivotDataResponse>) => void,
  error: (reason: string | undefined) => void,
  endpoint: string
): CancellationToken => {
  const controller = new AbortController();
  axios
    .post<ApiResponse<PivotDataResponse>>(`${baseUri}/${endpoint}/run`, toRunReportContract(body), {
      headers: { ...getDefaultHeaders(), ...getDataSetHeader(dataSetId) },
      signal: controller.signal,
    })
    .then((res) => done(res.data))
    .catch((reason: Error) => {
      if (reason.message === "canceled") {
        error("canceled");
        return;
      }
      const axiosError = reason as AxiosError<ApiResponse<unknown>>;
      const errorMessage = axiosError.response?.data?.error?.message;
      error(errorMessage);
    });
  return { cancel: () => controller.abort() };
};

const runChartReportBase = (
  dataSetId: string,
  body: RunChartReportRequest,
  done: (data: ApiResponse<ChartResponse>) => void,
  error: (reason: string | undefined) => void,
  endpoint: string
): CancellationToken => {
  const controller = new AbortController();
  axios
    .post<ApiResponse<ChartResponse>>(`${baseUri}/${endpoint}/run`, toRunReportContract(body), {
      headers: { ...getDefaultHeaders(), ...getDataSetHeader(dataSetId) },
      signal: controller.signal,
    })
    .then((res) => done(res.data))
    .catch((reason: Error) => {
      if (reason.message === "canceled") {
        error("canceled");
        return;
      }
      const axiosError = reason as AxiosError<ApiResponse<unknown>>;
      const errorMessage = axiosError.response?.data?.error?.message;
      error(errorMessage);
    });
  return { cancel: () => controller.abort() };
};

const requestBuildTabularExportBase = async (body: BuildExportRequest, endpoint: string) => {
  const { data } = await axios.post<ApiResponse<ExportResponse>>(
    `${baseUri}/${endpoint}/buildExport`,
    toBuildExportContract(body),
    {
      headers: builderHeaders(),
    }
  );
  return data;
};

const requestBuildPivotExportBase = async (body: BuildExportRequest, endpoint: string) => {
  const { data } = await axios.post<ApiResponse<ExportResponse>>(
    `${baseUri}/${endpoint}/buildExport`,
    toBuildExportContract(body),
    {
      headers: builderHeaders(),
    }
  );
  return data;
};

const requestRunPivotExportBase = async (body: RunExportRequest, endpoint: string) => {
  const { data } = await axios.post<ApiResponse<ExportResponse>>(
    `${baseUri}/${endpoint}/runExport`,
    toRunExportContract(body),
    {
      headers: builderHeaders(),
    }
  );
  return data;
};

const requestRunTabularExportBase = async (body: RunExportRequest, endpoint: string) => {
  const { data } = await axios.post<ApiResponse<ExportResponse>>(
    `${baseUri}/${endpoint}/runExport`,
    toRunExportContract(body),
    {
      headers: builderHeaders(),
    }
  );
  return data;
};

const requestBuildListExportBase = async (body: BuildExportRequest, endpoint: string) => {
  const { data } = await axios.post<ApiResponse<ExportResponse>>(
    `${baseUri}/${endpoint}/buildExport`,
    toBuildExportContract(body),
    {
      headers: builderHeaders(),
    }
  );
  return data;
};

const requestRunListExportBase = async (body: RunExportRequest, endpoint: string) => {
  const { data } = await axios.post<ApiResponse<ExportResponse>>(
    `${baseUri}/${endpoint}/runExport`,
    toRunExportContract(body),
    {
      headers: builderHeaders(),
    }
  );
  return data;
};

const getReportsApi = (endPoints: EndPoints) => {
  const getReports = async (
    { reportIds, reportType }: { reportIds?: string[]; reportType?: ReportType },
    signal?: AbortSignal
  ) => {
    const { data } = await axios.post<ApiResponse<ReportInfo[]>>(
      `${baseUri}/${endPoints.Reports}/get-reports`,
      reportIds ?? [],
      {
        headers: getDefaultHeaders(),
        params: { reportType },
        signal,
      }
    );
    return data;
  };

  const renameReport = async (request: RenameReportRequest) => {
    const { data } = await axios.put<ApiResponse<Report>>(
      `${baseUri}/${endPoints.Reports}/rename`,
      toUserInputContract(request, ["name"]),
      {
        headers: getDefaultHeaders(),
      }
    );
    return data;
  };

  const deleteReports = async (reports: DeleteReportsRequest) => {
    const { data } = await axios.patch<ApiResponse<boolean>>(
      `${baseUri}/${endPoints.Reports}/delete-reports`,
      { reports },
      {
        headers: getDefaultHeaders(),
      }
    );
    return data;
  };

  const validateReportName = async (request: ValidateReportNameRequest) => {
    const { data } = await axios.post<ApiResponse<NameValidationResponse>>(
      `${baseUri}/${endPoints.Reports}/validate-report-name`,
      toUserInputContract(request, ["name"]),
      {
        headers: getDefaultHeaders(),
      }
    );
    return data;
  };

  const getReportSharing = async (reportId: string) => {
    const { data } = await axios.get<ApiResponse<GetReportAuthorizationResponse>>(
      `${baseUri}/${endPoints.Reports}/${reportId}/sharing`,
      {
        headers: getDefaultHeaders(),
      }
    );
    return data;
  };

  const updateReportSharing = async (reportId: string, request: UpdateReportAuthorizationRequest) => {
    const { data } = await axios.put<ApiResponse<ReportAuthorizationInfo>>(
      `${baseUri}/${endPoints.Reports}/${reportId}/sharing`,
      request,
      {
        headers: getDefaultHeaders(),
      }
    );
    return data;
  };

  const updateReportsSharing = async (request: UpdateReportsAuthorizationRequest) => {
    const { data } = await axios.put<ApiResponse<BooleanOperationResult>>(
      `${baseUri}/${endPoints.Reports}/update-reports-sharing`,
      request,
      {
        headers: getDefaultHeaders(),
      }
    );
    return data;
  };

  const getReportAuthorizationInfo = async (reportId: string) => {
    const { data } = await axios.get<ApiResponse<ReportAuthorizationInfo>>(
      `${baseUri}/${endPoints.Reports}/${reportId}/sharing/authorization`,
      {
        headers: getDefaultHeaders(),
      }
    );
    return data;
  };

  const getReportHistory = async (reportId: string, signal?: AbortSignal) => {
    const { data } = await axios.get<ApiResponse<ReportHistoryItem[]>>(`${baseUri}/${endPoints.Reports}/history`, {
      headers: getDefaultHeaders(),
      params: { reportId },
      signal,
    });
    return data;
  };

  const runTileReport = (
    dataSetId: string,
    body: RunTileReportRequest,
    done: (data: ApiResponse<TileDataResponse>) => void,
    error: (reason: string | undefined) => void
  ): CancellationToken => {
    if (endPoints.Tile) {
      return runTileReportBase(dataSetId, body, done, error, endPoints.Tile);
    } else {
      throw new Error("Not Supported");
    }
  };

  const runTabularReport = <
    TBody extends RunTabularReportRequest,
    TResponse extends TabularDataResponse | ApiReportResponse,
  >(
    dataSetId: string,
    body: TBody,
    done: (data: ApiResponse<TResponse>) => void,
    error: (reason: string | undefined) => void
  ): CancellationToken => {
    if (endPoints.Tabular) {
      return runTabularReportBase(
        dataSetId,
        body,
        done,
        error,
        endPoints.Tabular === endPointsSetup.Ui.Tabular ? `${endPoints.Tabular}/snapshots` : endPoints.Tabular
      );
    } else {
      throw new Error("Not Supported");
    }
  };

  const runPivotReport = (
    dataSetId: string,
    body: RunPivotReportRequest,
    done: (data: ApiResponse<PivotDataResponse>) => void,
    error: (reason: string | undefined) => void
  ): CancellationToken => {
    if (endPoints.Pivot) {
      return runPivotReportBase(dataSetId, body, done, error, endPoints.Pivot);
    } else {
      throw new Error("Not Supported");
    }
  };

  const runChartReport = (
    dataSetId: string,
    body: RunChartReportRequest,
    done: (data: ApiResponse<ChartResponse>) => void,
    error: (reason: string | undefined) => void
  ): CancellationToken => {
    if (endPoints.Chart) {
      return runChartReportBase(dataSetId, body, done, error, endPoints.Chart);
    } else {
      throw new Error("Not Supported");
    }
  };

  const runListReport = async (dataSetId: string, body: RunListReportRequest, signal?: AbortSignal) => {
    if (endPoints.List) {
      return runListReportBase(dataSetId, body, endPoints.List, signal);
    }
    throw new Error("Not Supported");
  };

  const buildListReport = async (body: BuildListReportRequest, signal?: AbortSignal) => {
    if (endPoints.List) {
      return buildListReportBase(body, endPoints.List, signal);
    }
    throw new Error("Not Supported");
  };

  const runFinancialStatementReport = async (
    dataSetId: string,
    body: RunFinancialStatementReportRequest,
    signal?: AbortSignal
  ) => {
    if (endPoints.FinancialStatement) {
      return runFinancialStatementReportBase(dataSetId, body, endPoints.FinancialStatement, signal);
    }
    throw new Error("Not Supported");
  };

  const buildFinancialStatementReport = async (body: BuildFinancialStatementReportRequest, signal?: AbortSignal) => {
    if (endPoints.FinancialStatement) {
      return buildFinancialStatementReportBase(body, endPoints.FinancialStatement, signal);
    }
    throw new Error("Not Supported");
  };

  const undraftReports = async (reportIds: string[]) => {
    const { data } = await axios.post<ApiResponse<ReportInfo[]>>(
      `${baseUri}/${endPoints.Reports}/undraft-reports`,
      { reportIds },
      {
        headers: getDefaultHeaders(),
      }
    );
    return data;
  };

  const getReportConfigurationForRunning = async (reportId: string, signal?: AbortSignal) => {
    const { data } = await axios.get<ApiResponse<AnyReportConfigurationForRunning>>(
      `${baseUri}/${endPoints.Reports}/${reportId}/run-configuration`,
      {
        headers: getDefaultHeaders(),
        signal,
      }
    );
    return data;
  };

  const requestBuildTabularExport = async (body: BuildExportRequest) => {
    if (endPoints.Tabular) {
      return requestBuildTabularExportBase(body, endPoints.Tabular);
    } else {
      throw new Error("Not Supported");
    }
  };

  const requestBuildPivotExport = async (body: BuildExportRequest) => {
    if (endPoints.Pivot) {
      return requestBuildPivotExportBase(body, endPoints.Pivot);
    } else {
      throw new Error("Not Supported");
    }
  };

  const requestRunPivotExport = async (body: RunExportRequest) => {
    if (endPoints.Pivot) {
      return requestRunPivotExportBase(body, endPoints.Pivot);
    } else {
      throw new Error("Not Supported");
    }
  };

  const requestRunTabularExport = async (body: RunExportRequest) => {
    if (endPoints.Tabular) {
      return requestRunTabularExportBase(body, endPoints.Tabular);
    } else {
      throw new Error("Not Supported");
    }
  };

  const requestBuildListExport = async (body: BuildExportRequest) => {
    if (endPoints.List) {
      return requestBuildListExportBase(body, endPoints.List);
    }
    throw new Error("Not Supported");
  };

  const requestRunListExport = async (body: RunExportRequest) => {
    if (endPoints.List) {
      return requestRunListExportBase(body, endPoints.List);
    }
    throw new Error("Not Supported");
  };

  const addReport = async (report: Report) => {
    const { data } = await axios.post<ApiResponse<Report>>(
      `${baseUri}/${endPoints.Reports}`,
      toUserInputContract(report, ["name", "configuration"]),
      {
        headers: builderHeaders(),
      }
    );
    return data;
  };

  const createDraftReport = async () => {
    const { data } = await axios.post<ApiResponse<Report>>(
      `${baseUri}/${endPoints.Reports}/create-draft`,
      {},
      {
        headers: getDefaultHeaders(),
      }
    );
    return data;
  };

  const createDraftReportFromBiAgentConfiguration = async (configurationId: string, signal?: AbortSignal) => {
    const { data } = await axios.post<ApiResponse<string>>(
      `${baseUri}/${endPoints.Reports}/create-draft-from-configuration/${configurationId}`,
      {},
      {
        headers: getDefaultHeaders(),
        signal,
      }
    );
    return data;
  };

  const updateReport = async (report: Report) => {
    const { data } = await axios.put<ApiResponse<Report>>(
      `${baseUri}/${endPoints.Reports}`,
      toUserInputContract(report, ["name", "configuration"]),
      {
        headers: builderHeaders(),
      }
    );
    return data;
  };

  const getReport = async (reportId: string, mode: "run" | "edit") => {
    const { data } = await axios.get<ApiResponse<Report>>(`${baseUri}/${endPoints.Reports}/${reportId}?mode=${mode}`, {
      headers: builderHeaders(),
    });
    return data;
  };

  const buildTabularReport = <
    TBody extends BuildTabularReportRequest,
    TResponse extends TabularDataResponse | ApiReportResponse,
  >(
    body: TBody,
    done: (data: ApiResponse<TResponse>) => void,
    error: (reason: string | undefined) => void
  ): CancellationToken => {
    if (endPoints.Tabular) {
      return buildTabularReportBase(
        body,
        done,
        error,
        endPoints.Tabular === endPointsSetup.Ui.Tabular ? `${endPoints.Tabular}/snapshots` : endPoints.Tabular
      );
    } else {
      throw new Error("Not Supported");
    }
  };

  const buildPivotReport = (
    body: BuildPivotReportRequest,
    done: (data: ApiResponse<PivotDataResponse>) => void,
    error: (reason: string | undefined) => void
  ): CancellationToken => {
    if (endPoints.Pivot) {
      return buildPivotReportBase(body, done, error, endPoints.Pivot);
    } else {
      throw new Error("Not Supported");
    }
  };

  const buildTileReport = (
    body: BuildTileReportRequest,
    done: (data: ApiResponse<TileDataResponse>) => void,
    error: (reason: string | undefined) => void
  ): CancellationToken => {
    if (endPoints.Tile) {
      return buildTileReportBase(body, done, error, endPoints.Tile);
    } else {
      throw new Error("Not Supported");
    }
  };

  const buildChartReport = (
    body: BuildChartReportRequest,
    done: (data: ApiResponse<ChartResponse>) => void,
    error: (reason: string | undefined) => void
  ): CancellationToken => {
    if (endPoints.Chart) {
      return buildChartReportBase(body, done, error, endPoints.Chart);
    } else {
      throw new Error("Not Supported");
    }
  };

  const duplicateReport = async (request: DuplicateReportRequest) => {
    const { data } = await axios.post<ApiResponse<ReportInfo>>(
      `${baseUri}/${endPoints.Reports}/duplicate`,
      toUserInputContract(request, ["name"]),
      {
        headers: getDefaultHeaders(),
      }
    );
    return data;
  };

  const validateReport = (
    body: ValidateReportRequest,
    done: (data: ApiResponse<ReportValidationResponse>) => void,
    error: (reason: string) => void
  ): CancellationToken => {
    const controller = new AbortController();
    axios
      .post<ApiResponse<ReportValidationResponse>>(`${baseUri}/${endPoints.Reports}/validate-copying`, body, {
        headers: getDefaultHeaders(),
        signal: controller.signal,
      })
      .then((res) => done(res.data))
      .catch((reason: Error) => error(reason.message));
    return { cancel: () => controller.abort() };
  };

  const copyReport = (
    body: CopyReportRequest,
    done: (data: ApiResponse<CopyReportResponse>) => void,
    error: (reason: string) => void
  ): CancellationToken => {
    const controller = new AbortController();
    axios
      .post<ApiResponse<CopyReportResponse>>(`${baseUri}/${endPoints.Reports}/copy`, body, {
        headers: getDefaultHeaders(),
        signal: controller.signal,
      })
      .then((res) => done(res.data))
      .catch((reason: Error) => error(reason.message));
    return { cancel: () => controller.abort() };
  };

  return {
    addReport,
    createDraftReport,
    createDraftReportFromBiAgentConfiguration,
    updateReport,
    duplicateReport,
    validateReport,
    copyReport,
    buildTabularReport,
    buildPivotReport,
    buildTileReport,
    buildChartReport,
    getReport,
    requestRunTabularExport,
    requestBuildTabularExport,
    requestBuildPivotExport,
    requestRunPivotExport,
    requestBuildListExport,
    requestRunListExport,
    getReportConfigurationForRunning,
    getReportSharing,
    updateReportSharing,
    updateReportsSharing,
    getReportAuthorizationInfo,
    getReportHistory,
    runTabularReport,
    runPivotReport,
    runListReport,
    buildListReport,
    runFinancialStatementReport,
    buildFinancialStatementReport,
    runTileReport,
    runChartReport,
    getReports,
    renameReport,
    deleteReports,
    validateReportName,
    undraftReports,
  };
};

const getOrganizationReportsApi = (endpoint: EndPoints) => {
  const getOrganizationReports = async (signal?: AbortSignal) => {
    const { data } = await axios.get<ApiResponse<OrganizationReportInfo[]>>(
      `${baseUri}/${endpoint.Reports}/organization-reports`,
      {
        headers: getDefaultHeaders(),
        signal,
      }
    );
    return data;
  };

  const getOrganizationReportGroups = async (signal?: AbortSignal) => {
    const { data } = await axios.get<ApiResponse<Record<string, ReportGroup[]>>>(
      `${baseUri}/${endpoint.Reports}/organization-report-groups`,
      {
        headers: getDefaultHeaders(),
        signal,
      }
    );
    return data;
  };

  return {
    getOrganizationReports,
    getOrganizationReportGroups,
  };
};

export type ReportGroupApiClient = {
  addReportGroup: (request: AddReportGroupRequest) => Promise<ApiResponse<ReportGroup>>;
  updateReportGroup: (request: UpdateReportGroupRequest) => Promise<ApiResponse<ReportGroup>>;
  deleteReportGroup: (
    clientCode: string,
    groupId: string,
    reassignToGroupId?: string
  ) => Promise<ApiResponse<DeleteReportGroupResult>>;
  replaceReportGroup: (request: ReplaceReportGroupRequest) => Promise<ApiResponse<AnyObject>>;
  reorderReportGroups: (request: ReorderReportGroupsRequest) => Promise<ApiResponse<ReportGroupsResponse>>;
  getAccessibleReportGroups: (clientCode: string) => Promise<ApiResponse<ReportGroupsResponse>>;
  getAllReportGroups: (clientCode: string) => Promise<ApiResponse<ReportGroupsResponse>>;
  changeReportGroup: (request: ChangeReportGroupRequest) => Promise<ApiResponse<ReportResponse>>;
  changeReportsGroup: (request: ChangeReportsGroupRequest) => Promise<ApiResponse<BooleanOperationResult>>;
  addReportGroups: (groups: CreateReportGroup[]) => Promise<ApiResponse<ReportInfo[]>>;
  getReportGroupSharing: (clientCode: string, groupId: string) => Promise<ApiResponse<GetReportAuthorizationResponse>>;
  updateReportGroupSharing: (
    clientCode: string,
    groupId: string,
    request: UpdateReportGroupAuthorizationRequest
  ) => Promise<ApiResponse<ReportGroup>>;
};

const getReportGroupApi = (endpoint: string): ReportGroupApiClient => {
  const addReportGroup = async (request: AddReportGroupRequest) => {
    const { data } = await axios.post<ApiResponse<ReportGroup>>(
      `${baseUri}/${endpoint}/groups`,
      toUserInputContract(request, ["group"]),
      {
        headers: getDefaultHeaders(),
      }
    );
    return data;
  };

  const updateReportGroup = async (request: UpdateReportGroupRequest) => {
    const { data } = await axios.put<ApiResponse<ReportGroup>>(
      `${baseUri}/${endpoint}/groups`,
      toUserInputContract(request, ["group"]),
      {
        headers: getDefaultHeaders(),
      }
    );
    return data;
  };

  const deleteReportGroup = async (clientCode: string, groupId: string, reassignToGroupId?: string) => {
    const { data } = await axios.delete<ApiResponse<DeleteReportGroupResult>>(
      `${baseUri}/${endpoint}/groups/${clientCode}/${groupId}`,
      {
        headers: getDefaultHeaders(),
        params: reassignToGroupId ? { reassignToGroupId } : undefined,
      }
    );
    return data;
  };

  const replaceReportGroup = async (request: ReplaceReportGroupRequest) => {
    const { data } = await axios.put<ApiResponse<AnyObject>>(`${baseUri}/${endpoint}/groups/replace`, request, {
      headers: getDefaultHeaders(),
    });
    return data;
  };

  const reorderReportGroups = async (request: ReorderReportGroupsRequest) => {
    const { data } = await axios.put<ApiResponse<ReportGroupsResponse>>(
      `${baseUri}/${endpoint}/groups/reorder`,
      toUserInputContract(request, ["groups"]),
      {
        headers: getDefaultHeaders(),
      }
    );
    return data;
  };

  const getAccessibleReportGroups = async (clientCode: string) => {
    const { data } = await axios.get<ApiResponse<ReportGroupsResponse>>(`${baseUri}/${endpoint}/groups/${clientCode}`, {
      headers: getDefaultHeaders(),
    });
    return data;
  };

  const getAllReportGroups = async (clientCode: string) => {
    const { data } = await axios.get<ApiResponse<ReportGroupsResponse>>(
      `${baseUri}/${endpoint}/groups/${clientCode}/all`,
      {
        headers: getDefaultHeaders(),
      }
    );
    return data;
  };

  const changeReportGroup = async (request: ChangeReportGroupRequest) => {
    const { data } = await axios.put<ApiResponse<Report>>(`${baseUri}/${endpoint}/change-group`, request, {
      headers: getDefaultHeaders(),
    });
    return data;
  };

  const changeReportsGroup = async (request: ChangeReportsGroupRequest) => {
    const { data } = await axios.put<ApiResponse<BooleanOperationResult>>(
      `${baseUri}/${endpoint}/change-reports-group`,
      request,
      {
        headers: getDefaultHeaders(),
      }
    );
    return data;
  };

  const addReportGroups = async (groups: CreateReportGroup[]) => {
    const { data } = await axios.post<ApiResponse<ReportInfo[]>>(
      `${baseUri}/${endpoint}/groups/create`,
      { groups: groups.map((group) => toUserInputContract(group, ["name"])) },
      {
        headers: getDefaultHeaders(),
      }
    );
    return data;
  };

  const getReportGroupSharing = async (clientCode: string, groupId: string) => {
    const { data } = await axios.get<ApiResponse<GetReportAuthorizationResponse>>(
      `${baseUri}/${endpoint}/groups/${clientCode}/${groupId}/sharing`,
      {
        headers: getDefaultHeaders(),
      }
    );
    return data;
  };

  const updateReportGroupSharing = async (
    clientCode: string,
    groupId: string,
    request: UpdateReportGroupAuthorizationRequest
  ) => {
    const { data } = await axios.put<ApiResponse<ReportGroup>>(
      `${baseUri}/${endpoint}/groups/${clientCode}/${groupId}/sharing`,
      request,
      {
        headers: getDefaultHeaders(),
      }
    );
    return data;
  };

  return {
    addReportGroup,
    updateReportGroup,
    deleteReportGroup,
    replaceReportGroup,
    reorderReportGroups,
    getAccessibleReportGroups,
    getAllReportGroups,
    changeReportGroup,
    changeReportsGroup,
    addReportGroups,
    getReportGroupSharing,
    updateReportGroupSharing,
  };
};

export type FavoriteReportsApiClient = {
  getFavoriteReports: (clientCode: string) => Promise<ApiResponse<string[]>>;
  toggleFavoriteReport: (clientCode: string, reportId: string) => Promise<ApiResponse<string[]>>;
};

const getFavoriteReportsApi = (endpoint: string): FavoriteReportsApiClient => {
  const getFavoriteReports = async (clientCode: string) => {
    const { data } = await axios.get<ApiResponse<string[]>>(`${baseUri}/${endpoint}/favorites/${clientCode}`, {
      headers: getDefaultHeaders(),
    });
    return data;
  };

  const toggleFavoriteReport = async (clientCode: string, reportId: string) => {
    const { data } = await axios.put<ApiResponse<string[]>>(
      `${baseUri}/${endpoint}/favorites/${clientCode}/${reportId}`,
      null,
      {
        headers: getDefaultHeaders(),
      }
    );
    return data;
  };

  return { getFavoriteReports, toggleFavoriteReport };
};

export async function getDbSchemaStructure() {
  const { data } = await axios.get<ApiResponse<DbSchemaResponse>>(`${baseUri}/DevTools/db/structure`, {
    headers: getDefaultHeaders(),
  });
  return data;
}

export async function getDataModels() {
  const { data } = await axios.get<ApiResponse<DataModelResponse>>(`${baseUri}/DevTools/data-model`, {
    headers: getDefaultHeaders(),
  });
  return data;
}

export async function findPath(startModel: string, endModel: string) {
  const { data } = await axios.get<ApiResponse<string[]>>(
    `${baseUri}/DevTools/data-model/find-path?startModel=${startModel}&endModel=${endModel}`,
    {
      headers: getDefaultHeaders(),
    }
  );
  return data;
}

export async function getDimensions() {
  const { data } = await axios.get<ApiResponse<DimensionsResponse>>(`${baseUri}/DevTools/dimension-model`, {
    headers: getDefaultHeaders(),
  });
  return data;
}

export async function getQueryInfo(dimensionName: string) {
  const { data } = await axios.get<ApiResponse<DimensionQueryInfo>>(`${baseUri}/DevTools/dimension/${dimensionName}`, {
    headers: getDefaultHeaders(),
  });
  return data;
}

export async function getUsedReportingTables() {
  const { data } = await axios.get<ApiResponse<UsedTablesResponse>>(`${baseUri}/TableUsages`, {
    headers: getDefaultHeaders(),
  });
  return data;
}

const runWorkbook = async (payload: RunWorkbookRequest) => {
  const { data } = await axios.post<ApiResponse<RunWorkbookResponse>>(
    `${baseUri}/WorkbookRuns`,
    toUserInputContract(payload, ["name", "workbookFilters"]),
    {
      headers: getDefaultHeaders(),
    }
  );
  return data;
};

const searchWorkbookRuns = async (payload: SearchWorkbookRunsRequest, signal?: AbortSignal) => {
  const { data } = await axios.post<ApiResponse<ListWithTotal<WorkbookRun>>>(
    `${baseUri}/WorkbookRuns/search`,
    toUserInputContract(payload, ["searchTerm"]),
    {
      headers: getDefaultHeaders(),
      signal,
    }
  );
  return data;
};

const getWorkbookRun = async (runId: string, signal?: AbortSignal) => {
  const { data } = await axios.get<ApiResponse<WorkbookRun>>(`${baseUri}/WorkbookRuns/${runId}`, {
    headers: getDefaultHeaders(),
    signal,
  });
  return data;
};

const searchWorkbookTemplates = async (payload: SearchWorkbookTemplatesRequest, signal?: AbortSignal) => {
  const { data } = await axios.post<ApiResponse<ListWithTotal<WorkbookTemplate>>>(
    `${baseUri}/WorkbookTemplates/search`,
    toUserInputContract(payload, ["searchTerm"]),
    {
      headers: getDefaultHeaders(),
      signal,
    }
  );
  return data;
};

const getAllWorkbookTemplates = async (signal?: AbortSignal) => {
  const { data } = await axios.get<ApiResponse<WorkbookTemplate[]>>(`${baseUri}/WorkbookTemplates`, {
    headers: getDefaultHeaders(),
    signal,
  });
  return data;
};

const getWorkbookTemplate = async (templateId: string, signal?: AbortSignal) => {
  const { data } = await axios.get<ApiResponse<WorkbookTemplate>>(`${baseUri}/WorkbookTemplates/${templateId}`, {
    headers: getDefaultHeaders(),
    signal,
  });
  return data;
};

const getReportsAccessState = async (request: ReportsAccessStateRequest, signal?: AbortSignal) => {
  const { data } = await axios.post<ApiResponse<ReportAccessStateResponse>>(
    `${baseUri}/ExcelWorkbook/access-state`,
    request,
    {
      headers: getDefaultHeaders(),
      signal,
    }
  );
  return data;
};

const getWorkbookTemplateDownloadInfo = async (templateId: string, signal?: AbortSignal) => {
  const { data } = await axios.get<ApiResponse<FileDownloadInfo>>(
    `${baseUri}/WorkbookTemplates/download/${templateId}`,
    {
      headers: getDefaultHeaders(),
      signal,
    }
  );
  return data;
};

const getWorkbookRunDownloadInfo = async (runId: string, signal?: AbortSignal) => {
  const { data } = await axios.get<ApiResponse<FileDownloadInfo>>(`${baseUri}/WorkbookRuns/download/${runId}`, {
    headers: getDefaultHeaders(),
    signal,
  });
  return data;
};

const cancelWorkbookRuns = async (request: CancelWorkbookRunsRequest, signal?: AbortSignal) => {
  const { data } = await axios.post<ApiResponse<CancelJobsResponse>>(`${baseUri}/WorkbookRuns/cancel`, request, {
    headers: getDefaultHeaders(),
    signal,
  });
  return data;
};

const getRunsProgress = async (operationIds: string[], signal?: AbortSignal) => {
  const { data } = await axios.post<ApiResponse<WorkbookRunsProgress>>(
    `${baseUri}/WorkbookRuns/runs-progress`,
    { operationIds },
    {
      headers: getDefaultHeaders(),
      signal,
    }
  );
  return data;
};

const deleteWorkbookTemplates = async (request: DeleteWorkbookTemplatesRequest, signal?: AbortSignal) => {
  const { data } = await axios.post<ApiResponse<BooleanOperationResult>>(
    `${baseUri}/WorkbookTemplates/delete`,
    request,
    {
      headers: getDefaultHeaders(),
      signal,
    }
  );
  return data;
};

const deleteWorkbookRuns = async (request: DeleteWorkbookRunsRequest, signal?: AbortSignal) => {
  const { data } = await axios.post<ApiResponse<BooleanOperationResult>>(`${baseUri}/WorkbookRuns/delete`, request, {
    headers: getDefaultHeaders(),
    signal,
  });
  return data;
};

const downloadWorkbookRuns = async (
  request: DownloadWorkbooksRequest,
  fileName: string,
  signal?: AbortSignal
): Promise<ApiResponse<string>> => {
  const { data } = await axios.post<ApiResponse<string>>(`${baseUri}/WorkbookRuns/download/token`, request, {
    headers: getDefaultHeaders(),
    signal,
  });

  const token = data.data;
  return token
    ? createApiResponse(
        `${baseUri}/WorkbookRuns/download/multiple-runs?${qs.stringify({ fileNameUserInput: fileName, token, clientCode: getClientCode() })}`
      )
    : createErrorApiResponse(data.error);
};

const rerunWorkbookRun = async (runId: string, signal?: AbortSignal) => {
  const { data } = await axios.post<ApiResponse<RunWorkbookResponse>>(
    `${baseUri}/WorkbookRuns/${runId}/rerun`,
    {},
    {
      headers: getDefaultHeaders(),
      signal,
    }
  );
  return data;
};

const getParameterDictionary = async (reportType: ReportSource, parameterName: string, signal?: AbortSignal) => {
  const { data } = await axios.get<ApiResponse<ParameterDictionaryResponse>>(
    `${baseUri}/WorkbookRuns/dictionary/${reportType}/${parameterName}`,
    {
      headers: getDefaultHeaders(),
      signal,
    }
  );
  return data;
};

const uiOrganizationReportsApi = getOrganizationReportsApi(endPointsSetup.Ui);
const apiOrganizationReportsApi = getOrganizationReportsApi(endPointsSetup.Api);
const uiReportsApi = getReportsApi(endPointsSetup.Ui);
// IRR Details returns one schedule; snapshot delivery would discard its developer query results.
const buildCashFlowReport: typeof uiReportsApi.buildTabularReport = (body, done, error) =>
  buildTabularReportBase(body, done, error, endPointsSetup.Ui.Tabular);
const runCashFlowReport: typeof uiReportsApi.runTabularReport = (dataSetId, body, done, error) =>
  runTabularReportBase(dataSetId, body, done, error, endPointsSetup.Ui.Tabular);
const apiReportsApi = getReportsApi(endPointsSetup.Api);
const dashboardReportsApi = getReportsApi(endPointsSetup.Dashboard);
const systemReportsApi = getReportsApi(endPointsSetup.System);
const uiReportGroupApi = getReportGroupApi(endPointsSetup.Ui.Reports);
const apiReportGroupApi = getReportGroupApi(endPointsSetup.Api.Reports);
const systemReportGroupApi = getReportGroupApi(endPointsSetup.System.Reports);
const reportTemplateGroupApi = getReportGroupApi(reportTemplateGroups);
const uiFavoriteReportsApi = getFavoriteReportsApi(endPointsSetup.Ui.Reports);
const apiFavoriteReportsApi = getFavoriteReportsApi(endPointsSetup.Api.Reports);
const systemFavoriteReportsApi = getFavoriteReportsApi(endPointsSetup.System.Reports);

// SmartView Artifacts API
const smartViewArtifactsUri = `${baseUri}/SmartViewArtifacts`;

const getSmartViewArtifactUri = (id: string) => `${smartViewArtifactsUri}/${encodeURIComponent(id)}`;

const listSmartViewArtifacts = async (signal?: AbortSignal) => {
  const { data } = await axios.get<ApiResponse<SmartViewArtifact[]>>(smartViewArtifactsUri, {
    headers: getDefaultHeaders(),
    signal,
  });
  return data;
};

const createSmartViewArtifact = async (groupId: string, signal?: AbortSignal) => {
  const { data } = await axios.post<ApiResponse<SmartViewArtifact>>(
    smartViewArtifactsUri,
    { groupId },
    { headers: getDefaultHeaders(), signal }
  );
  return data;
};

const smartViewArtifactGroupApi = getReportGroupApi("SmartViewArtifacts");

const getSmartViewArtifact = async (id: string, signal?: AbortSignal) => {
  const { data } = await axios.get<ApiResponse<SmartViewArtifact>>(getSmartViewArtifactUri(id), {
    headers: getDefaultHeaders(),
    signal,
  });
  return data;
};

const listRecentSmartViewArtifacts = async (signal?: AbortSignal) => {
  const { data } = await axios.get<ApiResponse<RecentSmartViewArtifact[]>>(`${smartViewArtifactsUri}/recent`, {
    headers: getDefaultHeaders(),
    signal,
  });
  return data;
};

const recordSmartViewArtifactOpened = async (id: string, signal?: AbortSignal) => {
  const { data } = await axios.post<ApiResponse<BooleanOperationResult>>(
    `${getSmartViewArtifactUri(id)}/opened`,
    undefined,
    { headers: getDefaultHeaders(), signal }
  );
  return data;
};

const getSmartViewArtifactSharing = async (id: string, signal?: AbortSignal) => {
  const { data } = await axios.get<ApiResponse<GetReportAuthorizationResponse>>(
    `${getSmartViewArtifactUri(id)}/sharing`,
    {
      headers: getDefaultHeaders(),
      signal,
    }
  );
  return data;
};

const updateSmartViewArtifactSharing = async (
  id: string,
  request: UpdateReportAuthorizationRequest,
  signal?: AbortSignal
) => {
  const { data } = await axios.put<ApiResponse<ReportAuthorizationInfo>>(
    `${getSmartViewArtifactUri(id)}/sharing`,
    request,
    {
      headers: getDefaultHeaders(),
      signal,
    }
  );
  return data;
};

const deleteSmartViewArtifact = async (id: string, signal?: AbortSignal) => {
  const { data } = await axios.delete<ApiResponse<BooleanOperationResult>>(getSmartViewArtifactUri(id), {
    headers: getDefaultHeaders(),
    signal,
  });
  return data;
};

const listSmartViewArtifactVersions = async (id: string, signal?: AbortSignal) => {
  const { data } = await axios.get<ApiResponse<SmartViewArtifactVersionSummary[]>>(
    `${getSmartViewArtifactUri(id)}/versions`,
    { headers: getDefaultHeaders(), signal }
  );
  return data;
};

const getSmartViewArtifactVersion = async (id: string, version: number, signal?: AbortSignal) => {
  const { data } = await axios.get<ApiResponse<SmartViewArtifactVersion>>(
    `${getSmartViewArtifactUri(id)}/versions/${version}`,
    { headers: getDefaultHeaders(), signal }
  );
  return data;
};

const publishSmartViewArtifact = async (id: string, version: number, signal?: AbortSignal) => {
  const { data } = await axios.post<ApiResponse<SmartViewArtifact>>(
    `${getSmartViewArtifactUri(id)}/publish`,
    { version },
    { headers: getDefaultHeaders(), signal }
  );
  return data;
};

const renameSmartViewArtifact = async (id: string, name: string, signal?: AbortSignal) => {
  const { data } = await axios.post<ApiResponse<SmartViewArtifact>>(
    `${getSmartViewArtifactUri(id)}/rename`,
    { nameUserInput: name },
    { headers: getDefaultHeaders(), signal }
  );
  return data;
};

const restoreSmartViewArtifactVersion = async (id: string, version: number, signal?: AbortSignal) => {
  const { data } = await axios.post<ApiResponse<SmartViewArtifactVersion>>(
    `${getSmartViewArtifactUri(id)}/versions/${version}/restore`,
    undefined,
    { headers: getDefaultHeaders(), signal }
  );
  return data;
};

const getSmartViewArtifactPreviewUri = (id: string, version: number) =>
  `${getSmartViewArtifactUri(id)}/versions/${version}/preview`;

const getSmartViewArtifactPreview = async (id: string, version: number, signal?: AbortSignal) => {
  const response = await axios.get<Blob>(getSmartViewArtifactPreviewUri(id, version), {
    headers: getDefaultHeaders(),
    responseType: "blob",
    signal,
  });
  return response.status === 204 ? undefined : response.data;
};

const saveSmartViewArtifactPreview = async (id: string, version: number, image: Blob, signal?: AbortSignal) => {
  const form = new FormData();
  form.append("image", image, "preview.jpg");
  const { data } = await axios.put<ApiResponse<BooleanOperationResult>>(
    getSmartViewArtifactPreviewUri(id, version),
    form,
    {
      headers: getDefaultHeaders(),
      signal,
    }
  );
  return data;
};

const getSmartViewArtifactLogo = async (id: string, version: number, signal?: AbortSignal) => {
  const response = await axios.get<Blob>(`${getSmartViewArtifactUri(id)}/versions/${version}/logo`, {
    headers: getDefaultHeaders(),
    responseType: "blob",
    signal,
  });
  return response.status === 204 ? undefined : response.data;
};

const getSmartViewArtifactSourceUri = (id: string, version: number, sourceKey: string) =>
  `${getSmartViewArtifactUri(id)}/versions/${version}/sources/${encodeURIComponent(sourceKey)}`;

const runSmartViewArtifactSource = async (
  id: string,
  version: number,
  sourceKey: string,
  request: RunSmartViewArtifactSourceRequest,
  signal?: AbortSignal
) => {
  const { data } = await axios.post<ApiResponse<SmartViewArtifactSourceData>>(
    `${getSmartViewArtifactSourceUri(id, version, sourceKey)}/run`,
    request,
    { headers: getDefaultHeaders(), signal }
  );
  return data;
};

const getSmartViewArtifactSourceReport = async (
  id: string,
  version: number,
  sourceKey: string,
  mode: "run" | "edit",
  request: RunSmartViewArtifactSourceRequest,
  signal?: AbortSignal
) => {
  const { data } = await axios.post<ApiResponse<Report>>(
    `${getSmartViewArtifactSourceUri(id, version, sourceKey)}/report`,
    request,
    { headers: getDefaultHeaders(), params: { mode }, signal }
  );
  return data;
};

const getSmartViewArtifactSourceReportRunConfiguration = async (
  id: string,
  version: number,
  sourceKey: string,
  request: RunSmartViewArtifactSourceRequest,
  signal?: AbortSignal
) => {
  const { data } = await axios.post<ApiResponse<TabularReportConfigurationForRunning>>(
    `${getSmartViewArtifactSourceUri(id, version, sourceKey)}/report/run-configuration`,
    request,
    { headers: getDefaultHeaders(), signal }
  );
  return data;
};

const runSmartViewArtifactSourceReport = <
  TBody extends RunTabularReportRequest,
  TResponse extends TabularDataResponse | ApiReportResponse,
>(
  id: string,
  version: number,
  sourceKey: string,
  body: TBody,
  done: (data: ApiResponse<TResponse>) => void,
  error: (reason: string | undefined) => void
): CancellationToken => {
  const controller = new AbortController();
  axios
    .post<ApiResponse<TabularDataResponse>>(
      `${getSmartViewArtifactSourceUri(id, version, sourceKey)}/report/run`,
      toRunReportContract(body),
      { headers: getDefaultHeaders(), signal: controller.signal }
    )
    .then((res) => done(res.data as ApiResponse<TResponse>))
    .catch((reason: Error) => {
      if (reason.message === "canceled") {
        error("canceled");
        return;
      }
      const axiosError = reason as AxiosError<ApiResponse<unknown>>;
      error(axiosError.response?.data?.error?.message);
    });
  return { cancel: () => controller.abort() };
};

const requestSmartViewArtifactSourceReportExport = async (
  id: string,
  version: number,
  sourceKey: string,
  body: RunExportRequest
) => {
  const { data } = await axios.post<ApiResponse<ExportResponse>>(
    `${getSmartViewArtifactSourceUri(id, version, sourceKey)}/report/runExport`,
    toRunExportContract(body),
    { headers: getDefaultHeaders() }
  );
  return data;
};

const getSmartViewArtifactAgentModes = async (signal?: AbortSignal) => {
  const { data } = await axios.get<ApiResponse<SmartViewArtifactAgentMode[]>>(`${smartViewArtifactsUri}/agent/modes`, {
    headers: getDefaultHeaders(),
    signal,
  });
  return data;
};

async function* sendSmartViewArtifactMessageStream(
  id: string,
  request: SendSmartViewArtifactMessageRequest,
  signal?: AbortSignal
): AsyncGenerator<SmartViewArtifactAgentStreamEvent> {
  const form = new FormData();
  form.append("messageUserInput", request.messageUserInput);
  form.append("baseVersion", String(request.baseVersion));
  form.append("mode", request.mode);
  if (request.runtimeErrorUserInput !== undefined) {
    form.append("runtimeErrorUserInput", request.runtimeErrorUserInput);
  }
  if (request.pendingRequestUserInput !== undefined) {
    form.append("pendingRequestUserInput", request.pendingRequestUserInput);
  }
  if (request.pendingQuestionUserInput !== undefined) {
    form.append("pendingQuestionUserInput", request.pendingQuestionUserInput);
  }
  if (request.builderViewUserInput !== undefined) {
    form.append("builderViewUserInput", request.builderViewUserInput);
  }
  if (request.compare) {
    form.append("compare", "true");
  }
  request.images?.forEach((file) => form.append("images", file, file.name));
  request.measuredColours?.forEach(({ picture, hex, share }, index) => {
    form.append(`measuredColours[${index}].picture`, String(picture));
    form.append(`measuredColours[${index}].hex`, hex);
    form.append(`measuredColours[${index}].share`, String(share));
  });
  const response = await axios.post<ReadableStream>(`${getSmartViewArtifactUri(id)}/agent/send-message-stream`, form, {
    headers: getDefaultHeaders(),
    responseType: "stream",
    adapter: "fetch",
    signal,
  });
  yield* parseSseStream<SmartViewArtifactAgentStreamEvent>(response.data);
}

// BI Agent UI API
const sendBiAgentMessage = async (
  request: SendBiAgentMessageRequest,
  signal?: AbortSignal
): Promise<ApiResponse<BiAgentUiResponse>> => {
  const { data } = await axios.post<ApiResponse<BiAgentUiResponse>>(
    `${baseUri}/BiAgentUi/send-message`,
    toBiAgentMessageContract(request),
    {
      headers: { ...getDefaultHeaders(), ...dataSetHeader() },
      signal,
    }
  );
  return data;
};

type BiAgentStreamEvent = { type: "progress"; message: string } | { type: "result"; data: BiAgentUiResponse | null };

async function* sendBiAgentMessageStream(
  request: SendBiAgentMessageRequest,
  signal?: AbortSignal
): AsyncGenerator<BiAgentStreamEvent> {
  const response = await axios.post<ReadableStream>(
    `${baseUri}/BiAgentUi/send-message-stream`,
    toBiAgentMessageContract(request),
    {
      headers: { ...getDefaultHeaders(), ...dataSetHeader() },
      responseType: "stream",
      adapter: "fetch",
      signal,
    }
  );
  yield* parseSseStream<BiAgentStreamEvent>(response.data);
}

const biApi = {
  loadMeta,
  buildApArAgingSummaryReport,
  requestBuildApArAgingSummaryExport,
  buildApArAgingDetailReport,
  requestBuildApArAgingDetailExport,
  buildBalanceSheetReport,
  getBalanceSheetDimensionValues,
  requestBuildBalanceSheetExport,
  getDrillDownData,
  getDrillDownItem,
  getDrillDownConfiguration,
  getAttachedDocuments,
  getAttachedDocumentDownloadInfo,
  getChartOfAccountsDrillDown,
  isDrillDownCacheValid,
  requestDrillDownExport,
  requestCashflowExport,
  requestChartOfAccountsExport,
  checkExportReady,
  generateExportDownloadLink,
  addCustomMeasure,
  updateCustomMeasure,
  deleteCustomMeasure,
  lockCustomMeasure,
  unlockCustomMeasure,
  createMeasureSubgroup,
  updateMeasureSubgroup,
  deleteMeasureSubgroup,
  moveCustomMeasureToSubgroup,
  getUsedByReports,
  getMeasureDataSets,
  getLedgerBasis,
  getChartOfAccounts,
  getClientSettings,
  createReportTemplateDraft,
  addReportTemplate,
  crossFilter,
  deleteReportTemplates,
  duplicateReportTemplate,
  getClients,
  getCompanyReportTemplates,
  getOrganizationReportTemplates,
  getReportTemplateForCompany,
  getReportTemplateForOrganization,
  getTemplateFieldsData,
  loadDimensionDictionaries,
  updateReportTemplate,
  updateReportTemplateDataSource,
  updateReportTemplateStatus,
  validateTemplateName,
  validateUniqueReportNameByUsage,
  uiOrganizationReports: {
    getOrganizationReports: uiOrganizationReportsApi.getOrganizationReports,
    getOrganizationReportGroups: uiOrganizationReportsApi.getOrganizationReportGroups,
  },
  apiOrganizationReports: {
    getOrganizationReports: apiOrganizationReportsApi.getOrganizationReports,
    getOrganizationReportGroups: apiOrganizationReportsApi.getOrganizationReportGroups,
  },
  uiReports: {
    renameReport: uiReportsApi.renameReport,
    deleteReports: uiReportsApi.deleteReports,
    generateApiReport: generateApiReport,
    generateDashboardReport: generateDashboardReport,
    addReport: uiReportsApi.addReport,
    createDraftReport: uiReportsApi.createDraftReport,
    createDraftReportFromBiAgentConfiguration: uiReportsApi.createDraftReportFromBiAgentConfiguration,
    updateReport: uiReportsApi.updateReport,
    duplicateReport: uiReportsApi.duplicateReport,
    validateReport: uiReportsApi.validateReport,
    copyReport: uiReportsApi.copyReport,
    buildTabularReport: uiReportsApi.buildTabularReport,
    buildCashFlowReport,
    buildPivotReport: uiReportsApi.buildPivotReport,
    buildChartReport: uiReportsApi.buildChartReport,
    buildTileReport: uiReportsApi.buildTileReport,
    getReport: uiReportsApi.getReport,
    requestRunTabularExport: uiReportsApi.requestRunTabularExport,
    requestBuildTabularExport: uiReportsApi.requestBuildTabularExport,
    requestRunPivotExport: uiReportsApi.requestRunPivotExport,
    requestBuildPivotExport: uiReportsApi.requestBuildPivotExport,
    requestRunListExport: uiReportsApi.requestRunListExport,
    requestBuildListExport: uiReportsApi.requestBuildListExport,
    getReportAuthorizationInfo: uiReportsApi.getReportAuthorizationInfo,
    getReportHistory: uiReportsApi.getReportHistory,
    validateReportName: uiReportsApi.validateReportName,
    runTabularReport: uiReportsApi.runTabularReport,
    runCashFlowReport,
    getReportSharing: uiReportsApi.getReportSharing,
    updateReportSharing: uiReportsApi.updateReportSharing,
    updateReportsSharing: uiReportsApi.updateReportsSharing,
    getReports: uiReportsApi.getReports,
    runChartReport: uiReportsApi.runChartReport,
    runPivotReport: uiReportsApi.runPivotReport,
    runTileReport: uiReportsApi.runTileReport,
    runListReport: uiReportsApi.runListReport,
    buildListReport: uiReportsApi.buildListReport,
    runFinancialStatementReport: uiReportsApi.runFinancialStatementReport,
    buildFinancialStatementReport: uiReportsApi.buildFinancialStatementReport,
    getReportConfigurationForRunning: uiReportsApi.getReportConfigurationForRunning,
    addReportGroup: uiReportGroupApi.addReportGroup,
    updateReportGroup: uiReportGroupApi.updateReportGroup,
    deleteReportGroup: uiReportGroupApi.deleteReportGroup,
    replaceReportGroup: uiReportGroupApi.replaceReportGroup,
    reorderReportGroups: uiReportGroupApi.reorderReportGroups,
    getAccessibleReportGroups: uiReportGroupApi.getAccessibleReportGroups,
    getAllReportGroups: uiReportGroupApi.getAllReportGroups,
    changeReportGroup: uiReportGroupApi.changeReportGroup,
    changeReportsGroup: uiReportGroupApi.changeReportsGroup,
    addReportGroups: uiReportGroupApi.addReportGroups,
    getReportGroupSharing: uiReportGroupApi.getReportGroupSharing,
    updateReportGroupSharing: uiReportGroupApi.updateReportGroupSharing,
    getFavoriteReports: uiFavoriteReportsApi.getFavoriteReports,
    toggleFavoriteReport: uiFavoriteReportsApi.toggleFavoriteReport,
  },
  apiReports: {
    getReportHistory: apiReportsApi.getReportHistory,
    renameReport: apiReportsApi.renameReport,
    deleteReports: apiReportsApi.deleteReports,
    addReport: apiReportsApi.addReport,
    createDraftReport: apiReportsApi.createDraftReport,
    updateReport: apiReportsApi.updateReport,
    duplicateReport: apiReportsApi.duplicateReport,
    validateReport: apiReportsApi.validateReport,
    copyReport: apiReportsApi.copyReport,
    buildTabularReport: apiReportsApi.buildTabularReport,
    getReport: apiReportsApi.getReport,
    requestRunTabularExport: apiReportsApi.requestRunTabularExport,
    requestBuildTabularExport: apiReportsApi.requestBuildTabularExport,
    getReportAuthorizationInfo: apiReportsApi.getReportAuthorizationInfo,
    validateReportName: apiReportsApi.validateReportName,
    runTabularReport: apiReportsApi.runTabularReport,
    getReportSharing: apiReportsApi.getReportSharing,
    updateReportSharing: apiReportsApi.updateReportSharing,
    updateReportsSharing: apiReportsApi.updateReportsSharing,
    getReports: apiReportsApi.getReports,
    getReportConfigurationForRunning: apiReportsApi.getReportConfigurationForRunning,
    addReportGroup: apiReportGroupApi.addReportGroup,
    updateReportGroup: apiReportGroupApi.updateReportGroup,
    deleteReportGroup: apiReportGroupApi.deleteReportGroup,
    replaceReportGroup: apiReportGroupApi.replaceReportGroup,
    reorderReportGroups: apiReportGroupApi.reorderReportGroups,
    getAccessibleReportGroups: apiReportGroupApi.getAccessibleReportGroups,
    getAllReportGroups: apiReportGroupApi.getAllReportGroups,
    changeReportGroup: apiReportGroupApi.changeReportGroup,
    changeReportsGroup: apiReportGroupApi.changeReportsGroup,
    addReportGroups: apiReportGroupApi.addReportGroups,
    getReportGroupSharing: apiReportGroupApi.getReportGroupSharing,
    updateReportGroupSharing: apiReportGroupApi.updateReportGroupSharing,
    getFavoriteReports: apiFavoriteReportsApi.getFavoriteReports,
    toggleFavoriteReport: apiFavoriteReportsApi.toggleFavoriteReport,
  },
  dashboardReports: {
    getReportHistory: dashboardReportsApi.getReportHistory,
    updateReport: dashboardReportsApi.updateReport,
    addReport: dashboardReportsApi.addReport,
    createDraftReport: dashboardReportsApi.createDraftReport,
    getReport: dashboardReportsApi.getReport,
    getReportConfigurationForRunning: dashboardReportsApi.getReportConfigurationForRunning,
    getReports: dashboardReportsApi.getReports,
    renameReport: dashboardReportsApi.renameReport,
    deleteReports: dashboardReportsApi.deleteReports,
    validateReport: dashboardReportsApi.validateReport,
    validateReportName: dashboardReportsApi.validateReportName,
    getReportSharing: dashboardReportsApi.getReportSharing,
    updateReportSharing: dashboardReportsApi.updateReportSharing,
    getReportAuthorizationInfo: dashboardReportsApi.getReportAuthorizationInfo,
    runTileReport: dashboardReportsApi.runTileReport,
    runTabularReport: dashboardReportsApi.runTabularReport,
    runPivotReport: dashboardReportsApi.runPivotReport,
    runChartReport: dashboardReportsApi.runChartReport,
    undraftReports: dashboardReportsApi.undraftReports,
    buildTabularReport: dashboardReportsApi.buildTabularReport,
    buildPivotReport: dashboardReportsApi.buildPivotReport,
    buildChartReport: dashboardReportsApi.buildChartReport,
    buildTileReport: dashboardReportsApi.buildTileReport,
  },
  systemReports: {
    getReportHistory: systemReportsApi.getReportHistory,
    renameReport: systemReportsApi.renameReport,
    deleteReports: systemReportsApi.deleteReports,
    addReport: systemReportsApi.addReport,
    createDraftReport: systemReportsApi.createDraftReport,
    updateReport: systemReportsApi.updateReport,
    duplicateReport: systemReportsApi.duplicateReport,
    validateReport: systemReportsApi.validateReport,
    copyReport: systemReportsApi.copyReport,
    buildTabularReport: systemReportsApi.buildTabularReport,
    buildPivotReport: systemReportsApi.buildPivotReport,
    buildChartReport: systemReportsApi.buildChartReport,
    buildTileReport: systemReportsApi.buildTileReport,
    getReport: systemReportsApi.getReport,
    requestRunTabularExport: systemReportsApi.requestRunTabularExport,
    requestBuildTabularExport: systemReportsApi.requestBuildTabularExport,
    requestRunPivotExport: systemReportsApi.requestRunPivotExport,
    requestBuildPivotExport: systemReportsApi.requestBuildPivotExport,
    getReportAuthorizationInfo: systemReportsApi.getReportAuthorizationInfo,
    validateReportName: systemReportsApi.validateReportName,
    runTabularReport: systemReportsApi.runTabularReport,
    getReportSharing: systemReportsApi.getReportSharing,
    updateReportSharing: systemReportsApi.updateReportSharing,
    updateReportsSharing: systemReportsApi.updateReportsSharing,
    getReports: systemReportsApi.getReports,
    runChartReport: systemReportsApi.runChartReport,
    runPivotReport: systemReportsApi.runPivotReport,
    runTileReport: systemReportsApi.runTileReport,
    getReportConfigurationForRunning: systemReportsApi.getReportConfigurationForRunning,
    addReportGroup: systemReportGroupApi.addReportGroup,
    updateReportGroup: systemReportGroupApi.updateReportGroup,
    deleteReportGroup: systemReportGroupApi.deleteReportGroup,
    replaceReportGroup: systemReportGroupApi.replaceReportGroup,
    reorderReportGroups: systemReportGroupApi.reorderReportGroups,
    getAccessibleReportGroups: systemReportGroupApi.getAccessibleReportGroups,
    getAllReportGroups: systemReportGroupApi.getAllReportGroups,
    changeReportGroup: systemReportGroupApi.changeReportGroup,
    changeReportsGroup: systemReportGroupApi.changeReportsGroup,
    addReportGroups: systemReportGroupApi.addReportGroups,
    getReportGroupSharing: systemReportGroupApi.getReportGroupSharing,
    updateReportGroupSharing: systemReportGroupApi.updateReportGroupSharing,
    getFavoriteReports: systemFavoriteReportsApi.getFavoriteReports,
    toggleFavoriteReport: systemFavoriteReportsApi.toggleFavoriteReport,
  },
  uiReportGroup: uiReportGroupApi,
  apiReportGroup: apiReportGroupApi,
  systemReportGroup: systemReportGroupApi,
  reportTemplateGroup: reportTemplateGroupApi,
  runWorkbook: runWorkbook,
  searchWorkbookRuns: searchWorkbookRuns,
  searchWorkbookTemplates: searchWorkbookTemplates,
  getWorkbookTemplate: getWorkbookTemplate,
  getReportsAccessState: getReportsAccessState,
  getWorkbookTemplateDownloadInfo: getWorkbookTemplateDownloadInfo,
  getWorkbookRun: getWorkbookRun,
  getWorkbookRunDownloadInfo: getWorkbookRunDownloadInfo,
  cancelWorkbookRuns: cancelWorkbookRuns,
  getRunsProgress: getRunsProgress,
  deleteWorkbookTemplates: deleteWorkbookTemplates,
  deleteWorkbookRuns: deleteWorkbookRuns,
  downloadWorkbookRuns: downloadWorkbookRuns,
  rerunWorkbookRun: rerunWorkbookRun,
  getAllWorkbookTemplates: getAllWorkbookTemplates,
  getParameterDictionary: getParameterDictionary,
  searchDimensionDictionary: searchDimensionDictionary,
  biAgentUi: {
    sendMessage: sendBiAgentMessage,
    sendMessageStream: sendBiAgentMessageStream,
  },
  smartViewArtifacts: {
    list: listSmartViewArtifacts,
    create: createSmartViewArtifact,
    get: getSmartViewArtifact,
    listRecent: listRecentSmartViewArtifacts,
    recordOpened: recordSmartViewArtifactOpened,
    delete: deleteSmartViewArtifact,
    getSharing: getSmartViewArtifactSharing,
    updateSharing: updateSmartViewArtifactSharing,
    listVersions: listSmartViewArtifactVersions,
    getVersion: getSmartViewArtifactVersion,
    publish: publishSmartViewArtifact,
    rename: renameSmartViewArtifact,
    restore: restoreSmartViewArtifactVersion,
    getPreview: getSmartViewArtifactPreview,
    savePreview: saveSmartViewArtifactPreview,
    getLogo: getSmartViewArtifactLogo,
    runSource: runSmartViewArtifactSource,
    getSourceReport: getSmartViewArtifactSourceReport,
    getSourceReportRunConfiguration: getSmartViewArtifactSourceReportRunConfiguration,
    runSourceReport: runSmartViewArtifactSourceReport,
    requestSourceReportExport: requestSmartViewArtifactSourceReportExport,
    getAgentModes: getSmartViewArtifactAgentModes,
    sendMessageStream: sendSmartViewArtifactMessageStream,
  },
  smartViewArtifactGroups: {
    getAccessibleReportGroups: smartViewArtifactGroupApi.getAccessibleReportGroups,
    addReportGroup: smartViewArtifactGroupApi.addReportGroup,
    updateReportGroup: smartViewArtifactGroupApi.updateReportGroup,
    reorderReportGroups: smartViewArtifactGroupApi.reorderReportGroups,
    deleteReportGroup: smartViewArtifactGroupApi.deleteReportGroup,
    changeReportsGroup: smartViewArtifactGroupApi.changeReportsGroup,
    getReportGroupSharing: smartViewArtifactGroupApi.getReportGroupSharing,
    updateReportGroupSharing: smartViewArtifactGroupApi.updateReportGroupSharing,
  },
};

export default biApi;
